
import { Grid, CircularProgress, Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  callAPI,
  IsSmallScreen,
  Method,
  updateMainHeader,
} from "../../commonFunctions/CommonFunctions";
import PermissionDenied from "../../commonFunctions/PermissionDenied";
import { AllPermissions } from "../../commonFunctions/PermissionManger";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import WSMobileDataGridAdvanced, {
  IOrderColumns,
} from "../../Components/DataGrid/WSMobileDataGridAdvanced";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import ConfirmDialog, {
  useConfirmDialog,
} from "../../Components/Confirmation/ConfirmDialog";

interface IVendor {
  id: string;
  vendorName: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  gstNumber?: string;
  openingBalance?: number;
}

export const VendorList: React.FC<any> = React.memo(() => {
  const { add, edit, remove, view } = AllPermissions.getRolePermission;
  const navigate = useNavigate();
  const isMobile = IsSmallScreen();
  const { showMessage } = useSnackbar();

  const [vendors, setVendors] = useState<IVendor[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    open,
    title,
    onConfirm,
    handleClose,
    openConfirmDialog,
  } = useConfirmDialog();

  useEffect(() => {
    updateMainHeader("Vendors");
    breadCrumbsStore.updateBreadCrumbs([{ Vendors: "" }]);
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response: any = await callAPI("Vendor", Method.GET);
      const vendorList: IVendor[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setVendors(vendorList);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      showMessage("Error fetching vendors", "error");
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response: any = await callAPI("Vendor", Method.DELETE, null, id);
      if (!response || response?.success === true) {
        showMessage("Vendor deleted successfully", "success");
        fetchVendors();
      } else {
        showMessage(response?.message || "Error deleting vendor", "error");
      }
    } catch (error) {
      console.error("Error deleting vendor:", error);
      showMessage("Error deleting vendor", "error");
    } finally {
      handleClose();
    }
  };

  const handleAction = (action: string, row: IVendor) => {
    if (action === "Edit") {
      navigate(`/vendor/edit/${row.id}`);
    } else if (action === "Delete" && remove) {
      openConfirmDialog("Delete this vendor?", () => handleDelete(row.id));
    }
  };

  const columns: IColumns[] = [
    { header: "Vendor Name", fieldName: "vendorName", type: "default" },
    { header: "Contact Person", fieldName: "contactPerson", type: "default" },
    { header: "Email", fieldName: "email", type: "default" },
    { header: "Phone", fieldName: "phone", type: "default" },
    { header: "GST Number", fieldName: "gstNumber", type: "default" },
    { header: "Opening Balance", fieldName: "openingBalance", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
      actionCallback: handleAction,
    },
  ];

  const mobColumns: IOrderColumns[] = [
    { header: "Vendor Name", fieldName: "vendorName", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
      actionCallback: handleAction,
    },
  ];

  const handleAdd = () => navigate("/vendor/create");

  return (
    <Grid container spacing={2}>
      {view ? (
        <Grid item xs={12}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
              <CircularProgress />
            </Box>
          ) : !isMobile ? (
            <WSDataGrid
              id="vendorListGrid"
              title="Vendors"
              columns={columns}
              source={{
                filter: [],
                page: 10,
                pageSize: 1000,
                sort: [],
                url: "Vendor/search", // ✅ for server-side search
              }}
              searchBox={{ fieldName: "vendorName" }}
              addButton={add ? { label: "New", color: "secondary" } : undefined}
              onAddButtonClick={handleAdd}
            />
          ) : (
            <WSMobileDataGridAdvanced
              title="Vendors"
              id="vendorListGrid"
              columns={mobColumns}
              data={vendors}
              addButton={add ? { label: "New", color: "secondary" } : undefined}
              onAddButtonClick={handleAdd}
            />
          )}
        </Grid>
      ) : (
        <PermissionDenied />
      )}
      <ConfirmDialog open={open} title={title} onConfirm={onConfirm} onClose={handleClose} />
    </Grid>
  );
});
